import React, { useRef } from "react";
import { BookOpenText, School, ScrollText, Languages } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";

const sections = [
  {
    title: "Affiliation",
    icon: School,
    accentColor: "#f4c842",
    borderColor: "rgba(244,200,66,0.2)",
    glowColor: "rgba(244,200,66,0.08)",
    content:
      "This school is affiliated to the Council for the Indian School Certificate Examinations, New Delhi (vide no. PU-158) (CISCE). The school prepares students for the ICSE, from nursery up to class 12.",
  },
  {
    title: "Aim of the School",
    icon: ScrollText,
    accentColor: "#6366f1",
    borderColor: "rgba(99,102,241,0.2)",
    glowColor: "rgba(99,102,241,0.08)",
    content:
      "The aim of the school is to impart quality education for the all-round development of the child. Students are cultivated intellectually, morally, socially, professionally, and spiritually with emphasis on virtues like truthfulness and self-discipline.",
  },
  {
    title: "Course of Study",
    icon: BookOpenText,
    accentColor: "#8b5cf6",
    borderColor: "rgba(139,92,246,0.2)",
    glowColor: "rgba(139,92,246,0.08)",
    content:
      "Every student takes: English, Mathematics, Science (Physics, Chemistry & Biology), History, Civics, Geography, Physical Education, Computer Science, SUPW, Community Service, and Environmental Education.",
  },
  {
    title: "Medium of Instruction",
    icon: Languages,
    accentColor: "#06b6d4",
    borderColor: "rgba(6,182,212,0.2)",
    glowColor: "rgba(6,182,212,0.08)",
    content:
      "The medium of instruction is English with a high standard maintained for spoken English. Hindi as the National Language, and Punjabi as the Regional Language, are taught from the Pre-Primary level.",
  },
];

export default function KeyInformation() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <section
      ref={ref}
      className="relative py-20 px-6 md:px-16 overflow-hidden bg-[#020810]"
    >
      {/* Background Decor */}
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-blue-900/10 blur-[120px] rounded-full pointer-events-none -translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-14"
        >
          <div className="max-w-3xl">
            <span className="section-badge mb-4 inline-flex">Academic Framework</span>
            <h2
              className="font-black uppercase tracking-wider mb-4 whitespace-normal lg:whitespace-nowrap"
              style={{
                fontFamily: "Orbitron, sans-serif",
                fontSize: "clamp(1.4rem, 2.6vw, 2.2rem)",
                color: "#ffffff",
              }}
            >
              About Our <span className="text-gold">Academic Standards</span>
            </h2>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
              Founded on the principles of excellence and integrity, Galaxy Star Public School 
              provides a rigorous academic framework designed to prepare students for global challenges.
            </p>
          </div>

          <Link
            to="/facilities"
            className="group flex items-center gap-2.5 px-6 py-3.5 rounded-xl border border-gold/30 bg-gold/5 text-gold font-bold uppercase tracking-widest text-xs transition-all hover:bg-gold hover:text-black flex-shrink-0"
          >
            Explore Facilities
            <HiOutlineArrowNarrowRight className="text-base group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sections.map((section, i) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative group p-1 rounded-2xl overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-white/5 backdrop-blur-md p-7 md:p-8 rounded-[1.4rem] border border-white/5 h-full transition-transform duration-500 group-hover:-translate-y-1">
                  <div className="flex flex-col sm:flex-row gap-5">
                    {/* Icon Badge */}
                    <div 
                      className="w-13 h-13 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-500 group-hover:scale-105"
                      style={{ background: `${section.glowColor}`, border: `1px solid ${section.borderColor}`, color: section.accentColor }}
                    >
                      <Icon size={24} />
                    </div>

                    <div>
                      <h3
                        className="text-base sm:text-lg font-bold mb-2 uppercase tracking-wider"
                        style={{
                          fontFamily: "Orbitron, sans-serif",
                          color: section.accentColor,
                        }}
                      >
                        {section.title}
                      </h3>
                      <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  </div>
                  
                  {/* Decorative corner */}
                  <div className="absolute bottom-3 right-3 w-8 h-8 border-b border-r border-white/10 rounded-br-xl" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
